import { BankCodes, SecuritiesCodes } from '@revenuemarket/bank';
import { AccountStatus, ScrapeStatus } from '@revenuemarket/trade';
import { getDay } from '@revenuemarket/utils/days';
import dayjs, { Dayjs } from 'dayjs';

import { CardProjectionType } from '~apis/card';
import { FxRuleType } from '~components/ui/currencyexchange/FxRuleTypeStore';
import { AbstractBankAccount } from '~models/bank';
import { AccountType } from '~models/bank/enum';

export interface MonthlyCashFlowItem {
  category0: string | null;
  category1: string;
  category2: string;
  category3: string;
  categoryIds: number[];
  businessEntity: string;
  businessEntityId: number | null;
  customLabel: string;
  cashFlowType: string;
  isUnclassifiedCategory: boolean;
  isFavorite: boolean;
  amounts: Record<string, number>;
  percent: Record<string, number>;
  hierarchy: string[];
  categoryLabelKeys: string[];
  isCardPayment: boolean;
}

export interface SummaryRow {
  name: string;
  subName: string | null;
  amounts: { [key: string]: number };
}

export interface MonthlyCashFlowSetting {
  showCustomLabel: boolean;
  customLabelNames: string[];
  customLabels: number[];
  hideEmptyCategory: boolean;
  showFavoriteOnly: boolean;
  unit: string;
  hierarchy: string[];
  isDefault: boolean;
}

export type DateUnit = 'DATE' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR' | 'TOTAL';

export const FormatByDateUnit: Record<DateUnit, string> = {
  DATE: 'YYYY-MM-DD',
  MONTH: 'YYYY-MM',
  WEEK: 'YYYY-MM-DD 주',
  QUARTER: 'YYYY-[Q]Q',
  YEAR: 'YYYY',
  TOTAL: '',
};

export const formateBaseDateHeader = (baseDate: Dayjs, dateUnit: DateUnit) => {
  if (dateUnit === 'TOTAL') {
    return '전체 기간';
  }
  return (
    baseDate.format(FormatByDateUnit[dateUnit]) +
    (dateUnit === 'DATE' ? ` (${getDay(dayjs(baseDate))})` : '')
  );
};

export interface CashFlowRequest {
  companyId: string;
  startDate: Dayjs;
  endDate: Dayjs;
  rangeType: DateUnit;
  fxRuleType: FxRuleType;
  cardProjectionType?: CardProjectionType;
}

export interface BankAccountCashFlow extends AbstractBankAccount {
  accountId: number;
  startBalance: number;
  startWithdrawableBalance: number;
  deposit: number;
  withdrawal: number;
  transferInternal: number;
  endBalance: number;
  endWithdrawableBalance: number;
  startBalanceKrw: number;
  startWithdrawableBalanceKrw: number;
  depositKrw: number;
  withdrawalKrw: number;
  transferInternalKrw: number;
  endBalanceKrw: number;
  endWithdrawableBalanceKrw: number;
  currencyExchangeRateImpact: number;
  currencyCode: string;
  accountStatus: AccountStatus;
  scrapeStatus: ScrapeStatus;
  isPurged: boolean;
  originalAccountAlias?: string;
}

export interface SimpleBankAccount {
  accountId: number;
  accountNumber: string;
  accountType: AccountType;
  displayAccountNumber?: string;
  organizationCode: BankCodes | SecuritiesCodes;
  status: AccountStatus;
}

export enum CashFlowType {
  IN = 'IN',
  OUT = 'OUT',
}

interface SummaryBase {
  count: number;
  amount: number;
  flow: CashFlowType;
}

export interface BankAccountSummaryByCategory extends SummaryBase {
  name: string;
  categoryLabelKey: string;
  isCardPayment: boolean;
}

export interface BankAccountSummaryByBusinessEntity extends SummaryBase {
  businessEntityId: number;
  businessEntityName: string;
}

export interface BankAccountSummaryByBusinessEntityResponse {
  cashInSummary: BankAccountSummaryByBusinessEntity[];
  cashOutSummary: BankAccountSummaryByBusinessEntity[];
}

export interface BankAccountSummaryByCategoryResponse {
  cashInSummary: BankAccountSummaryByCategory[];
  cashOutSummary: BankAccountSummaryByCategory[];
}

export interface BankAccountCashFlowOverview {
  displayAccountCashFlows: BankAccountCashFlow[];
  hiddenAccountCashFlows: BankAccountCashFlow[];
}

export interface BankAccountCashFlowRequest {
  startDate: string;
  endDate: string;
  excludeAbnormal?: boolean;
  fxRuleType?: FxRuleType;
}

export interface UpdateBankAccountsOrderRequest {
  bankAccountSettings: {
    orders: number[];
    hidden: number[];
  };
}
